"use client";

import { use, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "@/src/lib/next-compat";
import * as api from "@/src/api/sdk.gen";

type BankResponse = {
  moduleTitle: string;
  bank: { total: number; perLesson: Array<{ lessonId: number; title: string; count: number }> };
  moduleQuizRow: { status: string; totalQuestions: number } | null;
};

export default function AdminModuleQuizPage({
  params,
}: {
  params?: Promise<{ id: string; moduleId: string }>;
} = {}) {
  const routerParams = useParams<{ id: string; moduleId: string }>();
  const courseId = routerParams.id || "";
  const moduleId = routerParams.moduleId || "";
  const [data, setData] = useState<BankResponse | null>(null);
  const [sample, setSample] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { data: resData, error: err } = await api.getAdminModuleQuizApiV1AdminModulesModuleIdQuizGet({
          path: { module_id: Number(moduleId) },
        });
        if (err || !resData) {
          setError("Failed to load module quiz bank");
        } else {
          setData(resData as unknown as BankResponse);
        }
      } catch (err) {
        setError("Failed to load module quiz bank");
      }
    })();
  }, [moduleId]);

  const runSample = async () => {
    try {
      const { data: resData } = await api.generateAdminModuleQuizSampleApiV1AdminModulesModuleIdQuizPost({
        path: { module_id: Number(moduleId) },
      });
      setSample(resData);
    } catch (err) {
      console.error("Failed to run sample:", err);
    }
  };

  if (error) return <p className="p-8 text-red-600">{error}</p>;
  if (!data) return <p className="p-8">Loading…</p>;

  return (
    <div className="max-w-3xl mx-auto p-8 space-y-6">
      <Link to={`/admin/courses/${courseId}`} className="text-indigo-600 text-sm">
        ← Back to course
      </Link>
      <h1 className="text-2xl font-bold">{data.moduleTitle} — Module Quiz</h1>
      <p className="text-gray-600">
        Live bank: <strong>{data.bank.total}</strong> questions · Status:{" "}
        {data.moduleQuizRow?.status ?? "not started"}
      </p>
      <ul className="border rounded-lg divide-y">
        {data.bank.perLesson.map((l) => (
          <li key={l.lessonId} className="px-4 py-2 flex justify-between">
            <span>{l.title}</span>
            <span className="text-gray-500">{l.count}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => void runSample()}
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium"
      >
        Generate sample draw
      </button>
      {sample ? (
        <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto max-h-96">
          {JSON.stringify(sample, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}
